import { useEffect, useMemo, useRef, useState, FormEvent } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, X, ShoppingBag, CalendarDays } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOpciones } from "../hooks/useOpciones";
import { Spinner } from "../components/ui/Spinner";
import { PageHeader } from "../components/ui/PageHeader";
import { Modal } from "../components/ui/Modal";
import { Badge } from "../components/ui/Badge";

// ---------------------------------------------------------------------------
// Configuración visual de la cuadrícula de horarios
// ---------------------------------------------------------------------------
const HORA_INICIO = 7; // la grilla empieza a las 7:00
const HORA_FIN = 20; // y termina a las 20:00 (8pm)
const PX_POR_HORA = 64;
const ALTURA_GRILLA = (HORA_FIN - HORA_INICIO) * PX_POR_HORA;
// Alto FIJO del encabezado de cada columna de día (y del espaciador de la
// columna de horas, que debe medir exactamente lo mismo). Antes el alto se
// dejaba "flotar" según el contenido, y como el día de hoy tiene un número
// dentro de un círculo (más alto que el texto normal de los demás días), su
// encabezado quedaba unos pixeles más alto que el resto — eso recorría
// hacia abajo el inicio de la cuadrícula de ESA columna nada más, y las
// líneas de las horas se veían "descuadradas" respecto a las otras
// columnas. Al fijar el alto, todas las columnas arrancan exactamente en el
// mismo pixel sin importar el contenido del encabezado.
const ALTURA_ENCABEZADO = 52;

const tiposServicio = [
  { value: "CONSULTA", label: "Consulta" },
  { value: "VACUNACION", label: "Vacunación" },
  { value: "CIRUGIA", label: "Cirugía" },
  { value: "GROOMING", label: "Grooming" },
  { value: "ENTRENAMIENTO", label: "Entrenamiento" },
  { value: "DOMICILIO", label: "Domicilio" },
  { value: "PASEO", label: "Paseo" },
  { value: "HOSPEDAJE", label: "Hospedaje" },
  { value: "OTRO", label: "Otro" },
];

const estados = ["PENDIENTE", "CONFIRMADA", "COMPLETADA", "CANCELADA", "NO_ASISTIO"];

// Opciones de hora en múltiplos de 30 minutos para el selector de "Hora de
// inicio" — reemplaza el <input type="datetime-local"> anterior, cuyo
// selector nativo de horas/minutos con scroll era incómodo de usar.
const OPCIONES_HORA = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

// Opciones de duración para reemplazar el campo "Fin": el negocio elige
// cuánto dura la cita en vez de tener que calcular y escribir la hora de
// término. Por default una cita nueva dura 1 hora.
const OPCIONES_DURACION = [
  { minutos: 15, etiqueta: "15 minutos" },
  { minutos: 30, etiqueta: "30 minutos" },
  { minutos: 45, etiqueta: "45 minutos" },
  { minutos: 60, etiqueta: "1 hora" },
  { minutos: 90, etiqueta: "1 hora 30 minutos" },
  { minutos: 120, etiqueta: "2 horas" },
  { minutos: 150, etiqueta: "2 horas 30 minutos" },
  { minutos: 180, etiqueta: "3 horas" },
  { minutos: 240, etiqueta: "4 horas" },
];
const DURACION_DEFAULT_MINUTOS = 60;

const colorBadge: Record<string, "verde" | "amarillo" | "rojo" | "azul" | "gris"> = {
  PENDIENTE: "amarillo",
  CONFIRMADA: "azul",
  COMPLETADA: "verde",
  CANCELADA: "rojo",
  NO_ASISTIO: "gris",
};

// Clases del bloque de cada cita dentro de la cuadrícula, según su estado.
const estiloBloque: Record<string, string> = {
  PENDIENTE: "bg-amber-100 border-amber-300 text-amber-800",
  CONFIRMADA: "bg-sky-100 border-sky-300 text-sky-800",
  COMPLETADA: "bg-emerald-100 border-emerald-300 text-emerald-800",
  CANCELADA: "bg-red-100 border-red-300 text-red-700 line-through opacity-70",
  NO_ASISTIO: "bg-slate-200 border-slate-300 text-slate-600",
};

// ---------------------------------------------------------------------------
// Utilidades de fecha (todo en hora LOCAL, nunca UTC, para no desfasar
// horarios según el huso horario del navegador).
// ---------------------------------------------------------------------------
function inicioDeDia(fecha: Date) {
  const d = new Date(fecha);
  d.setHours(0, 0, 0, 0);
  return d;
}
function finDeDia(fecha: Date) {
  const d = new Date(fecha);
  d.setHours(23, 59, 59, 999);
  return d;
}
function inicioSemana(fecha: Date) {
  const d = new Date(fecha);
  const dia = d.getDay(); // 0 = domingo ... 6 = sábado
  const diff = (dia === 0 ? -6 : 1) - dia; // ajustar para que la semana empiece en lunes
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function mismoDia(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function claveDia(f: Date) {
  return `${f.getFullYear()}-${f.getMonth()}-${f.getDate()}`;
}
// Formatea una fecha en hora LOCAL al formato que espera <input type="date">.
function aInputFecha(fecha: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())}`;
}
function redondearHora(horaDecimal: number) {
  const redondeada = Math.round(horaDecimal * 2) / 2; // al medio-hora más cercano
  return Math.min(Math.max(redondeada, HORA_INICIO), HORA_FIN - 0.5);
}
function formatHoraDecimal(horaDecimal: number) {
  const h = Math.floor(horaDecimal);
  const m = Math.round((horaDecimal - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
// Convierte una hora en formato 24 horas (7, 14, 19...) a un texto en
// formato 12 horas con a.m./p.m. — a los negocios les costaba leer "14:00" o
// "19:30" de un vistazo, así que en toda la Agenda (la cuadrícula y el
// selector de "Hora de inicio" al crear una cita) se muestra siempre en este
// formato. El valor que se guarda y se manda al servidor sigue siendo
// 24 horas, esto es solo lo que se le enseña a la persona.
function etiquetaHoraAMPM(hora24: number, minutos = 0): string {
  const periodo = hora24 < 12 ? "a.m." : "p.m.";
  const hora12 = hora24 % 12 === 0 ? 12 : hora24 % 12;
  const minutosTexto = minutos === 0 ? "" : `:${String(minutos).padStart(2, "0")}`;
  return `${hora12}${minutosTexto} ${periodo}`;
}
// Igual que arriba, pero recibe la hora ya como texto "HH:MM" (formato de
// las opciones del selector y de los valores del formulario).
function etiquetaHoraTextoAMPM(horaTexto: string): string {
  const [h, m] = horaTexto.split(":").map(Number);
  return etiquetaHoraAMPM(h, m);
}

// Reparte las citas de un día en "columnas" cuando se traslapan en horario,
// para que se muestren una al lado de la otra en vez de encimadas.
function distribuirColumnas(citasDia: any[]) {
  const orden = [...citasDia].sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime());
  const finColumnas: number[] = [];
  const asignaciones: { cita: any; col: number }[] = [];
  for (const c of orden) {
    const inicio = new Date(c.fechaInicio).getTime();
    const fin = new Date(c.fechaFin).getTime();
    let col = finColumnas.findIndex((f) => f <= inicio);
    if (col === -1) {
      col = finColumnas.length;
      finColumnas.push(fin);
    } else {
      finColumnas[col] = fin;
    }
    asignaciones.push({ cita: c, col });
  }
  const totalCols = Math.max(finColumnas.length, 1);
  return asignaciones.map((a) => ({ ...a, totalCols }));
}

function posicionBloque(cita: any) {
  const inicio = new Date(cita.fechaInicio);
  const fin = new Date(cita.fechaFin);
  const horaInicioDec = Math.max(HORA_INICIO, inicio.getHours() + inicio.getMinutes() / 60);
  const horaFinCruda = fin.getHours() + fin.getMinutes() / 60;
  const horaFinDec = Math.min(HORA_FIN, horaFinCruda < horaInicioDec ? HORA_FIN : horaFinCruda);
  const top = (horaInicioDec - HORA_INICIO) * PX_POR_HORA;
  const alto = Math.max((horaFinDec - horaInicioDec) * PX_POR_HORA, 22);
  return { top, alto };
}

// Igual que posicionBloque pero para el "fantasma" que se muestra mientras
// se arrastra una cita a un horario nuevo, antes de soltarla.
function posicionGhost(horaInicioDec: number, duracionMs: number) {
  const duracionHoras = duracionMs / 3_600_000;
  const horaFinDec = Math.min(HORA_FIN, horaInicioDec + duracionHoras);
  const top = (horaInicioDec - HORA_INICIO) * PX_POR_HORA;
  const alto = Math.max((horaFinDec - horaInicioDec) * PX_POR_HORA, 22);
  return { top, alto };
}

const formularioVacio = {
  clienteId: "",
  mascotaId: "",
  veterinarioId: "",
  tipoServicio: "CONSULTA",
  estado: "PENDIENTE",
  fecha: "",
  hora: "",
  duracionMinutos: DURACION_DEFAULT_MINUTOS,
  notas: "",
  serviciosSeleccionados: [] as any[],
};

export default function Agenda() {
  const { soloLectura } = useAuth();
  const [vista, setVista] = useState<"dia" | "semana">("semana");
  const [fechaBase, setFechaBase] = useState(new Date());
  const [citas, setCitas] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recargaTick, setRecargaTick] = useState(0);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [citaEditando, setCitaEditando] = useState<any | null>(null);
  const [form, setForm] = useState<any>(formularioVacio);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState<string | null>(null);
  const [productoSel, setProductoSel] = useState("");

  // --- Arrastrar y soltar para reprogramar citas directamente en la
  // cuadrícula (estilo Google Calendar) ---
  // Referencias a la cuadrícula de cada columna de día, para saber en qué
  // día y a qué hora quedaría la cita según la posición del cursor.
  const gridRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Info de la cita que se está arrastrando — se guarda en un ref (no en
  // estado) porque cambia en cada pixel de movimiento y no necesita
  // disparar un re-render por sí sola.
  const dragInfoRef = useRef<{
    citaId: string;
    duracionMs: number;
    startClientX: number;
    startClientY: number;
    offsetEnBloquePx: number;
    diaOrigenIndex: number;
    moved: boolean;
  } | null>(null);
  // Esto sí es estado porque necesita re-renderizar: la previsualización
  // (el bloque punteado) de dónde quedaría la cita si se suelta ahí.
  const [previewDrag, setPreviewDrag] = useState<{ citaId: string; diaIndex: number; horaDecimal: number } | null>(null);
  const [arrastrandoId, setArrastrandoId] = useState<string | null>(null);

  const { opciones: clientes } = useOpciones("/clientes", (c) => c.nombre);
  const { raw: mascotasTodas } = useOpciones("/mascotas", (m) => m.nombre);
  const { opciones: veterinarios } = useOpciones("/usuarios", (u) => u.nombre, (u) => ["VETERINARIO", "ADMIN"].includes(u.rol));
  // Catálogo de productos y servicios (Baño premium, Vacuna tipo 1, etc.) que
  // se puede agregar de forma opcional a la cita, sin ser obligatorio.
  const { opciones: productosOpciones, raw: productosRaw } = useOpciones("/productos", (p) => `${p.nombre} · $${p.precioVenta.toLocaleString("es-MX")}`);

  const mascotasFiltradas = useMemo(
    () => (form.clienteId ? mascotasTodas.filter((m: any) => m.clienteId === form.clienteId) : mascotasTodas),
    [mascotasTodas, form.clienteId]
  );

  const dias = useMemo(() => {
    if (vista === "dia") return [fechaBase];
    const lunes = inicioSemana(fechaBase);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(lunes);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [vista, fechaBase]);

  // Se limpian las referencias a las columnas viejas cada vez que cambia la
  // cantidad/orden de días visibles (día <-> semana, o se navega a otra
  // semana), para que el cálculo de arrastre nunca compare contra un nodo
  // que ya no corresponde a ese índice.
  useEffect(() => {
    gridRefs.current = {};
  }, [dias]);

  const rango = useMemo(() => {
    if (vista === "dia") return { desde: inicioDeDia(fechaBase), hasta: finDeDia(fechaBase) };
    const lunes = inicioSemana(fechaBase);
    const domingo = new Date(lunes);
    domingo.setDate(domingo.getDate() + 6);
    return { desde: lunes, hasta: finDeDia(domingo) };
  }, [vista, fechaBase]);

  useEffect(() => {
    let activo = true;
    setCargando(true);
    setError(null);
    api
      .get("/citas", { params: { desde: rango.desde.toISOString(), hasta: rango.hasta.toISOString() } })
      .then((res) => {
        if (activo) setCitas(res.data.items ?? []);
      })
      .catch((err) => activo && setError(mensajeError(err)))
      .finally(() => activo && setCargando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango.desde.getTime(), rango.hasta.getTime(), recargaTick]);

  const citasPorDia = useMemo(() => {
    const mapa: Record<string, any[]> = {};
    for (const c of citas) {
      const clave = claveDia(new Date(c.fechaInicio));
      (mapa[clave] ??= []).push(c);
    }
    return mapa;
  }, [citas]);

  function anterior() {
    setFechaBase((f) => {
      const d = new Date(f);
      d.setDate(d.getDate() - (vista === "dia" ? 1 : 7));
      return d;
    });
  }
  function siguiente() {
    setFechaBase((f) => {
      const d = new Date(f);
      d.setDate(d.getDate() + (vista === "dia" ? 1 : 7));
      return d;
    });
  }
  function irAHoy() {
    setFechaBase(new Date());
  }

  function abrirNueva(dia: Date, horaDecimal: number) {
    if (soloLectura) return;
    const horaRedondeada = redondearHora(horaDecimal);
    setCitaEditando(null);
    setForm({
      ...formularioVacio,
      fecha: aInputFecha(dia),
      hora: formatHoraDecimal(horaRedondeada),
      duracionMinutos: DURACION_DEFAULT_MINUTOS,
      serviciosSeleccionados: [],
    });
    setProductoSel("");
    setErrorForm(null);
    setModalAbierto(true);
  }

  function abrirEdicion(cita: any) {
    const inicio = new Date(cita.fechaInicio);
    const fin = new Date(cita.fechaFin);
    const duracionMinutos = Math.max(15, Math.round((fin.getTime() - inicio.getTime()) / 60000));
    setCitaEditando(cita);
    setForm({
      clienteId: cita.clienteId ?? "",
      mascotaId: cita.mascotaId ?? "",
      veterinarioId: cita.veterinarioId ?? "",
      tipoServicio: cita.tipoServicio ?? "CONSULTA",
      estado: cita.estado ?? "PENDIENTE",
      fecha: aInputFecha(inicio),
      hora: `${String(inicio.getHours()).padStart(2, "0")}:${String(inicio.getMinutes()).padStart(2, "0")}`,
      duracionMinutos,
      notas: cita.notas ?? "",
      serviciosSeleccionados: cita.serviciosSeleccionados ?? [],
    });
    setProductoSel("");
    setErrorForm(null);
    setModalAbierto(true);
  }

  // Mueve una cita a un nuevo día/hora arrastrándola en la cuadrícula,
  // conservando su duración original. Se usa un PUT parcial (solo
  // fechaInicio/fechaFin) para no tener que reenviar el resto de la cita.
  async function reprogramar(citaId: string, nuevaFechaInicio: Date, duracionMs: number) {
    const original = citas.find((c: any) => c.id === citaId);
    if (original) {
      // Soltar la cita en el mismo horario no es un cambio: no se guarda ni se avisa.
      if (new Date(original.fechaInicio).getTime() === nuevaFechaInicio.getTime()) return;
      const fmt = (d: Date) =>
        d.toLocaleString("es-MX", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
      // Mover una cita le manda un aviso de WhatsApp/correo al cliente, así
      // que se pide confirmación para que un arrastre accidental no lo dispare.
      if (!confirm(`¿Mover la cita de ${fmt(new Date(original.fechaInicio))} a ${fmt(nuevaFechaInicio)}?\n\nSe le avisará al cliente del cambio.`)) return;
    }
    const nuevaFechaFin = new Date(nuevaFechaInicio.getTime() + duracionMs);
    try {
      await api.put(`/citas/${citaId}`, {
        fechaInicio: nuevaFechaInicio.toISOString(),
        fechaFin: nuevaFechaFin.toISOString(),
      });
      recargar();
    } catch (err) {
      setError(mensajeError(err));
    }
  }

  function agregarServicio() {
    if (soloLectura || !productoSel) return;
    const p = productosRaw.find((x: any) => x.id === productoSel);
    if (!p) return;
    setForm((f: any) => {
      const existentes: any[] = f.serviciosSeleccionados ?? [];
      const yaExiste = existentes.find((s) => s.productoId === p.id);
      const actualizados = yaExiste
        ? existentes.map((s) => (s.productoId === p.id ? { ...s, cantidad: s.cantidad + 1 } : s))
        : [...existentes, { productoId: p.id, nombre: p.nombre, cantidad: 1, precioUnitario: p.precioVenta }];
      return { ...f, serviciosSeleccionados: actualizados };
    });
    setProductoSel("");
  }

  function quitarServicio(productoId: string) {
    setForm((f: any) => ({
      ...f,
      serviciosSeleccionados: (f.serviciosSeleccionados ?? []).filter((s: any) => s.productoId !== productoId),
    }));
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setErrorForm(null);
    try {
      // form.fecha ("2026-07-18") + form.hora ("10:00") son ambos valores en
      // hora LOCAL del navegador (sin zona horaria). Si se mandaran tal cual,
      // el backend (que corre en UTC) los interpretaría como si ya fueran
      // UTC y la cita se guardaría varias horas antes de lo seleccionado.
      // Por eso se combinan en un Date (que sí toma la zona horaria local del
      // navegador) y se convierten explícitamente a un instante absoluto con
      // toISOString() antes de enviarlos.
      const inicio = new Date(`${form.fecha}T${form.hora}`);
      const fin = new Date(inicio.getTime() + Number(form.duracionMinutos) * 60000);
      const { fecha, hora, duracionMinutos, ...resto } = form;
      const payload = {
        ...resto,
        fechaInicio: inicio.toISOString(),
        fechaFin: fin.toISOString(),
      };
      if (citaEditando) {
        await api.put(`/citas/${citaEditando.id}`, payload);
      } else {
        await api.post("/citas", payload);
      }
      setModalAbierto(false);
      recargar();
    } catch (err) {
      setErrorForm(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  async function eliminar() {
    if (!citaEditando) return;
    if (!window.confirm("¿Eliminar esta cita? Esta acción no se puede deshacer.")) return;
    setGuardando(true);
    setErrorForm(null);
    try {
      await api.delete(`/citas/${citaEditando.id}`);
      setModalAbierto(false);
      recargar();
    } catch (err) {
      setErrorForm(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  function recargar() {
    setRecargaTick((t) => t + 1);
  }

  const hoy = new Date();
  const tituloRango =
    vista === "dia"
      ? fechaBase.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
      : (() => {
          const lunes = dias[0];
          const domingo = dias[6];
          const mismoMes = lunes.getMonth() === domingo.getMonth();
          const inicio = lunes.toLocaleDateString("es-MX", { day: "numeric", month: mismoMes ? undefined : "short" });
          const fin = domingo.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
          return `${inicio} – ${fin}`;
        })();

  return (
    <div>
      <PageHeader
        titulo="Agenda"
        descripcion="Calendario visual de citas. Haz clic en un espacio libre para agendar."
        icono={<CalendarDays size={20} />}
        acciones={
          <div className="flex items-center gap-2">
            <div className="flex rounded-xl border border-slate-200 p-0.5">
              <button
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${vista === "dia" ? "bg-brand-600 text-white" : "text-slate-500"}`}
                onClick={() => setVista("dia")}
              >
                Día
              </button>
              <button
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${vista === "semana" ? "bg-brand-600 text-white" : "text-slate-500"}`}
                onClick={() => setVista("semana")}
              >
                Semana
              </button>
            </div>
            {!soloLectura && (
              <button className="btn-primary" onClick={() => abrirNueva(vista === "dia" ? fechaBase : new Date(), 9)}>
                <Plus size={16} /> Nueva cita
              </button>
            )}
          </div>
        }
      />

      <div className="card mb-4 flex items-center justify-between p-3">
        <button className="btn-ghost !px-2" onClick={anterior}>
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <p className="text-sm font-semibold capitalize text-slate-700">{tituloRango}</p>
          <button className="text-xs text-brand-600 hover:underline" onClick={irAHoy}>
            Ir a hoy
          </button>
        </div>
        <button className="btn-ghost !px-2" onClick={siguiente}>
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {estados.map((es) => (
          <span key={es} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full border ${estiloBloque[es]}`} />
            {es.replace("_", " ").toLowerCase()}
          </span>
        ))}
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="card overflow-x-auto">
        {cargando ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="flex min-w-[640px]">
            {/* Columna de horas — con fondo propio para distinguirse
            claramente de las columnas de días, como en Google Calendar. */}
            <div className="w-14 flex-shrink-0 border-r border-slate-300 bg-slate-100">
              <div style={{ height: ALTURA_ENCABEZADO }} className="sticky top-0 z-10 border-b border-slate-300 bg-slate-100" />
              {Array.from({ length: HORA_FIN - HORA_INICIO }, (_, i) => HORA_INICIO + i).map((h) => (
                <div key={h} style={{ height: PX_POR_HORA }} className="relative -translate-y-2 text-right text-[11px] font-medium text-slate-500">
                  <span className="pr-2">{etiquetaHoraAMPM(h)}</span>
                </div>
              ))}
            </div>

            {/* Columnas de días */}
            {dias.map((dia, diaIndex) => {
              const citasDelDia = citasPorDia[claveDia(dia)] ?? [];
              const disposicion = distribuirColumnas(citasDelDia);
              const esHoy = mismoDia(dia, hoy);
              const minutosAhora = hoy.getHours() + hoy.getMinutes() / 60;
              const mostrarLineaAhora = esHoy && minutosAhora >= HORA_INICIO && minutosAhora <= HORA_FIN;
              const citaEnPreview = previewDrag && previewDrag.diaIndex === diaIndex ? citas.find((c) => c.id === previewDrag.citaId) : null;

              return (
                <div key={dia.toISOString()} className="flex-1 min-w-[110px] border-l border-slate-300">
                  <div
                    style={{ height: ALTURA_ENCABEZADO }}
                    className={`sticky top-0 z-10 flex flex-col items-center justify-center gap-0.5 border-b-2 text-center ${
                      esHoy ? "border-brand-500 bg-brand-100 text-brand-700" : "border-slate-300 bg-slate-100 text-slate-600"
                    }`}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide">{dia.toLocaleDateString("es-MX", { weekday: "short" })}</p>
                    <p className={`text-sm font-semibold leading-none ${esHoy ? "flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-white" : ""}`}>
                      {dia.getDate()}
                    </p>
                  </div>

                  <div
                    ref={(el) => {
                      gridRefs.current[diaIndex] = el;
                    }}
                    className="relative cursor-pointer"
                    style={{ height: ALTURA_GRILLA }}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const offsetY = e.clientY - rect.top;
                      const horaDecimal = redondearHora(offsetY / PX_POR_HORA + HORA_INICIO);
                      abrirNueva(dia, horaDecimal);
                    }}
                  >
                    {/* Líneas de la cuadrícula (cada media hora), con una
                    franja de fondo alternada por cada hora completa para que
                    sea fácil ubicar una hora de un vistazo — estilo Google
                    Calendar. Las horas completas llevan una línea más
                    marcada que las medias horas. */}
                    {Array.from({ length: (HORA_FIN - HORA_INICIO) * 2 }, (_, i) => i).map((i) => (
                      <div
                        key={i}
                        style={{ height: PX_POR_HORA / 2 }}
                        className={`border-t ${i % 2 === 0 ? "border-slate-300" : "border-slate-200"} ${
                          Math.floor(i / 2) % 2 === 0 ? "bg-slate-50" : "bg-white"
                        }`}
                      />
                    ))}

                    {esHoy && <div className="pointer-events-none absolute inset-0 z-0 bg-brand-50/40" />}

                    {mostrarLineaAhora && (
                      <div
                        className="pointer-events-none absolute left-0 right-0 z-20 flex items-center gap-1"
                        style={{ top: (minutosAhora - HORA_INICIO) * PX_POR_HORA }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        <span className="h-px flex-1 bg-red-400" />
                      </div>
                    )}

                    {disposicion.map(({ cita, col, totalCols }) => {
                      const { top, alto } = posicionBloque(cita);
                      return (
                        <button
                          key={cita.id}
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => {
                            if (soloLectura) return;
                            const bloqueRect = e.currentTarget.getBoundingClientRect();
                            dragInfoRef.current = {
                              citaId: cita.id,
                              duracionMs: new Date(cita.fechaFin).getTime() - new Date(cita.fechaInicio).getTime(),
                              startClientX: e.clientX,
                              startClientY: e.clientY,
                              offsetEnBloquePx: e.clientY - bloqueRect.top,
                              diaOrigenIndex: diaIndex,
                              moved: false,
                            };
                            e.currentTarget.setPointerCapture(e.pointerId);
                          }}
                          onPointerMove={(e) => {
                            const info = dragInfoRef.current;
                            if (!info || info.citaId !== cita.id) return;
                            const dx = e.clientX - info.startClientX;
                            const dy = e.clientY - info.startClientY;
                            if (!info.moved && Math.hypot(dx, dy) < 14) return; // ignora micro-movimientos (temblor de mano/dedo): así el clic normal sigue abriendo la cita y no se mueve por accidente
                            if (!info.moved) {
                              info.moved = true;
                              setArrastrandoId(cita.id);
                            }
                            for (const [idxStr, el] of Object.entries(gridRefs.current)) {
                              if (!el) continue;
                              const r = el.getBoundingClientRect();
                              if (e.clientX >= r.left && e.clientX <= r.right) {
                                const idx = Number(idxStr);
                                const offsetY = e.clientY - r.top - info.offsetEnBloquePx;
                                const horaDecimal = redondearHora(offsetY / PX_POR_HORA + HORA_INICIO);
                                setPreviewDrag((prev) =>
                                  prev && prev.citaId === cita.id && prev.diaIndex === idx && prev.horaDecimal === horaDecimal
                                    ? prev
                                    : { citaId: cita.id, diaIndex: idx, horaDecimal }
                                );
                                break;
                              }
                            }
                          }}
                          onPointerUp={(e) => {
                            const info = dragInfoRef.current;
                            dragInfoRef.current = null;
                            if (!info || info.citaId !== cita.id) return;
                            if (!info.moved) {
                              // No se movió: fue un clic normal, se abre para editar.
                              abrirEdicion(cita);
                              return;
                            }
                            setArrastrandoId(null);
                            if (previewDrag && previewDrag.citaId === cita.id) {
                              const diaDestino = dias[previewDrag.diaIndex];
                              const nuevaFechaInicio = new Date(diaDestino);
                              const horas = Math.floor(previewDrag.horaDecimal);
                              const minutos = Math.round((previewDrag.horaDecimal - horas) * 60);
                              nuevaFechaInicio.setHours(horas, minutos, 0, 0);
                              reprogramar(cita.id, nuevaFechaInicio, info.duracionMs);
                            }
                            setPreviewDrag(null);
                          }}
                          onPointerCancel={() => {
                            dragInfoRef.current = null;
                            setArrastrandoId(null);
                            setPreviewDrag(null);
                          }}
                          style={{
                            top,
                            height: alto,
                            left: `calc(${(col / totalCols) * 100}% + 2px)`,
                            width: `calc(${(1 / totalCols) * 100}% - 4px)`,
                            touchAction: soloLectura ? undefined : "none",
                          }}
                          className={`absolute overflow-hidden rounded-lg border px-1.5 py-1 text-left text-[11px] leading-tight shadow-sm transition hover:brightness-95 ${estiloBloque[cita.estado] ?? "bg-slate-100 border-slate-300"} ${arrastrandoId === cita.id ? "opacity-40" : ""} ${!soloLectura ? "cursor-grab active:cursor-grabbing" : ""}`}
                        >
                          <p className="font-semibold">
                            {new Date(cita.fechaInicio).toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", hour12: true })}
                          </p>
                          <p className="truncate">{cita.mascota?.nombre ?? cita.cliente?.nombre ?? "Cita"}</p>
                        </button>
                      );
                    })}

                    {citaEnPreview && previewDrag && (
                      <div
                        className="pointer-events-none absolute inset-x-1 z-30 overflow-hidden rounded-lg border-2 border-dashed border-brand-500 bg-brand-100/80 px-1.5 py-1 text-[11px] font-semibold leading-tight text-brand-700"
                        style={posicionGhost(previewDrag.horaDecimal, dragInfoRef.current?.duracionMs ?? (new Date(citaEnPreview.fechaFin).getTime() - new Date(citaEnPreview.fechaInicio).getTime()))}
                      >
                        <p>{etiquetaHoraAMPM(Math.floor(previewDrag.horaDecimal), Math.round((previewDrag.horaDecimal - Math.floor(previewDrag.horaDecimal)) * 60))}</p>
                        <p className="truncate">{citaEnPreview.mascota?.nombre ?? citaEnPreview.cliente?.nombre ?? "Cita"}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal open={modalAbierto} onClose={() => setModalAbierto(false)} title={citaEditando ? "Editar cita" : "Nueva cita"}>
        <form onSubmit={guardar} className="space-y-4">
          <div>
            <label className="label">Cliente *</label>
            <select
              className="input"
              required
              disabled={soloLectura}
              value={form.clienteId}
              onChange={(e) => setForm({ ...form, clienteId: e.target.value, mascotaId: "" })}
            >
              <option value="">Selecciona...</option>
              {clientes.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Mascota</label>
            <select className="input" disabled={soloLectura} value={form.mascotaId} onChange={(e) => setForm({ ...form, mascotaId: e.target.value })}>
              <option value="">Selecciona...</option>
              {mascotasFiltradas.map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Veterinario / responsable</label>
            <select
              className="input"
              disabled={soloLectura}
              value={form.veterinarioId}
              onChange={(e) => setForm({ ...form, veterinarioId: e.target.value })}
            >
              <option value="">Sin asignar</option>
              {veterinarios.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Tipo de servicio</label>
            <select
              className="input"
              disabled={soloLectura}
              value={form.tipoServicio}
              onChange={(e) => setForm({ ...form, tipoServicio: e.target.value })}
            >
              {tiposServicio.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label mb-1 block">
              <ShoppingBag size={14} className="inline -mt-0.5 mr-1" />
              Servicios / productos (opcional)
            </label>
            <div className="flex gap-2">
              <select
                className="input flex-1"
                disabled={soloLectura}
                value={productoSel}
                onChange={(e) => setProductoSel(e.target.value)}
              >
                <option value="">Selecciona...</option>
                {productosOpciones.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button type="button" className="btn-secondary !px-3" disabled={soloLectura || !productoSel} onClick={agregarServicio}>
                <Plus size={16} />
              </button>
            </div>
            {(form.serviciosSeleccionados ?? []).length > 0 && (
              <ul className="mt-2 divide-y divide-slate-50 rounded-xl border border-slate-100">
                {form.serviciosSeleccionados.map((s: any) => (
                  <li key={s.productoId} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className="text-slate-700">
                      {s.nombre} <span className="text-slate-400">x{s.cantidad}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">${(s.precioUnitario * s.cantidad).toLocaleString("es-MX")}</span>
                      {!soloLectura && (
                        <button type="button" className="text-red-500" onClick={() => quitarServicio(s.productoId)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
                <li className="flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-700">
                  <span>Total</span>
                  <span>
                    $
                    {form.serviciosSeleccionados
                      .reduce((acc: number, s: any) => acc + s.precioUnitario * s.cantidad, 0)
                      .toLocaleString("es-MX")}
                  </span>
                </li>
              </ul>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Fecha *</label>
              <input
                type="date"
                className="input"
                required
                disabled={soloLectura}
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Hora de inicio *</label>
              <select
                className="input"
                required
                disabled={soloLectura}
                value={form.hora}
                onChange={(e) => setForm({ ...form, hora: e.target.value })}
              >
                <option value="">Selecciona...</option>
                {OPCIONES_HORA.map((h) => (
                  <option key={h} value={h}>
                    {etiquetaHoraTextoAMPM(h)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Duración *</label>
            <select
              className="input"
              required
              disabled={soloLectura}
              value={form.duracionMinutos}
              onChange={(e) => setForm({ ...form, duracionMinutos: Number(e.target.value) })}
            >
              {OPCIONES_DURACION.map((d) => (
                <option key={d.minutos} value={d.minutos}>
                  {d.etiqueta}
                </option>
              ))}
            </select>
          </div>
          {citaEditando && (
            <div>
              <label className="label">Estado</label>
              <div className="flex items-center gap-2">
                <select
                  className="input !w-auto"
                  disabled={soloLectura}
                  value={form.estado}
                  onChange={(e) => setForm({ ...form, estado: e.target.value })}
                >
                  {estados.map((es) => (
                    <option key={es} value={es}>
                      {es.replace("_", " ")}
                    </option>
                  ))}
                </select>
                <Badge color={colorBadge[form.estado]}>{form.estado.replace("_", " ")}</Badge>
              </div>
            </div>
          )}
          <div>
            <label className="label">Notas</label>
            <textarea
              className="input"
              rows={2}
              disabled={soloLectura}
              value={form.notas}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
            />
          </div>

          {errorForm && <p className="text-sm text-red-600">{errorForm}</p>}

          {!soloLectura && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
              {citaEditando ? (
                <button type="button" className="btn-ghost text-red-600" onClick={eliminar} disabled={guardando}>
                  <Trash2 size={16} /> Eliminar
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button type="button" className="btn-secondary" onClick={() => setModalAbierto(false)}>
                  <X size={16} /> Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={guardando}>
                  {guardando && <Spinner className="h-4 w-4 text-white" />}
                  {citaEditando ? "Guardar cambios" : "Agendar"}
                </button>
              </div>
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
}
